import { CommonModule, DatePipe } from "@angular/common";
import {
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormControl, FormsModule, ReactiveFormsModule } from "@angular/forms";
import {
  TuiAlertService,
  TuiAppearance,
  TuiButton,
  TuiLoader,
  TuiTextfield,
} from "@taiga-ui/core";
import { TuiPagination, TuiTabs } from "@taiga-ui/kit";
import { TuiNavigation, TuiSearch } from "@taiga-ui/layout";
import { TuiTable } from "@taiga-ui/addon-table";
import {
  TuiInputDateModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { TuiDay } from "@taiga-ui/cdk";
import {
  ISessionLog,
  SessionLogService,
} from "../../core/services/session-log.service";
import { Subscription, interval } from "rxjs";
import { loadListState, saveListState } from "../../shared/utils/list-state";

@Component({
  selector: "app-login-activity",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TuiAppearance,
    TuiButton,
    TuiLoader,
    TuiNavigation,
    TuiPagination,
    TuiSearch,
    TuiTable,
    TuiTabs,
    TuiTextfield,
    TuiInputDateModule,
    TuiTextfieldControllerModule,
  ],
  templateUrl: "./login-activity.component.html",
  styleUrl: "./login-activity.component.scss",
  providers: [DatePipe],
})
export class LoginActivityComponent implements OnInit, OnDestroy {
  private sessionLogService = inject(SessionLogService);
  private alerts = inject(TuiAlertService);
  private cdr = inject(ChangeDetectorRef);
  protected readonly tabs = ["Past Logs", "Active Sessions"];
  protected activeTabIndex = 0;

  // Day filter (defaults to today). Sets dayStart=00:00 and dayEnd=23:59 in
  // local timezone.
  protected dayControl = new FormControl<TuiDay>(
    TuiDay.fromLocalNativeDate(new Date())
  );
  protected searchControl = new FormControl("");

  isLoading = false;
  sessions: ISessionLog[] = [];
  activeSessions: ISessionLog[] = [];

  pageIndex = 1;
  pageSize = 25;
  total = 0;
  totalPages = 0;

  private pollSub: Subscription | null = null;

  private readonly listStateKey = "login-activity-list";

  ngOnInit(): void {
    this.restoreListState();
    this.loadPastLogs();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  private restoreListState(): void {
    const restored = loadListState(this.listStateKey, {
      activeTabIndex: this.activeTabIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      dayIso: null as string | null,
      searchText: "" as string,
    });
    this.activeTabIndex = restored.activeTabIndex;
    this.pageIndex = restored.pageIndex;
    this.pageSize = restored.pageSize;
    if (restored.dayIso) {
      try {
        this.dayControl.setValue(
          TuiDay.fromLocalNativeDate(new Date(restored.dayIso)),
          { emitEvent: false }
        );
      } catch {
        /* ignore - keep today */
      }
    }
    this.searchControl.setValue(restored.searchText || "", { emitEvent: false });
  }

  private rememberListState(): void {
    const day = this.dayControl.value;
    saveListState(this.listStateKey, {
      activeTabIndex: this.activeTabIndex,
      pageIndex: this.pageIndex,
      pageSize: this.pageSize,
      dayIso: day ? day.toLocalNativeDate().toISOString() : null,
      searchText: this.searchControl.value || "",
    });
  }

  protected onTabChange(index: number): void {
    this.activeTabIndex = index;
    this.stopPolling();
    if (index === 0) {
      this.loadPastLogs();
    } else {
      this.loadActiveSessions();
      // Poll every 10 seconds for the Active Sessions tab.
      this.pollSub = interval(10000).subscribe(() => this.loadActiveSessions());
    }
  }

  private stopPolling(): void {
    if (this.pollSub) {
      this.pollSub.unsubscribe();
      this.pollSub = null;
    }
  }

  loadPastLogs(): void {
    this.rememberListState();
    this.isLoading = true;
    const day = this.dayControl.value;
    const native = day ? day.toLocalNativeDate() : new Date();
    const dayStart = new Date(
      native.getFullYear(),
      native.getMonth(),
      native.getDate(),
      0,
      0,
      0
    );
    const dayEnd = new Date(
      native.getFullYear(),
      native.getMonth(),
      native.getDate(),
      23,
      59,
      59
    );

    this.sessionLogService
      .getPagedSessionLogs({
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        filters: {
          dayStart: dayStart.toISOString(),
          dayEnd: dayEnd.toISOString(),
          searchText: this.searchControl.value || undefined,
        },
      })
      .subscribe({
        next: (res: any) => {
          if (res?.success && Array.isArray(res.data)) {
            const facet = res.data[0] || { metadata: [], data: [] };
            this.sessions = facet.data || [];
            this.total = facet.metadata?.[0]?.total || 0;
            this.totalPages = Math.max(
              1,
              Math.ceil(this.total / this.pageSize)
            );
          }
          this.isLoading = false;
          this.cdr.markForCheck();
        },
        error: (err) => {
          console.error("Failed to load session logs", err);
          this.isLoading = false;
          this.alerts
            .open("Failed to load session logs", { appearance: "error" })
            .subscribe();
        },
      });
  }

  loadActiveSessions(): void {
    this.sessionLogService.getActiveSessions().subscribe({
      next: (res: any) => {
        if (res?.success && Array.isArray(res.data)) {
          this.activeSessions = res.data;
          this.cdr.markForCheck();
        }
      },
      error: (err) => {
        console.error("Failed to load active sessions", err);
      },
    });
  }

  applyFilters(): void {
    this.pageIndex = 1;
    this.loadPastLogs();
  }

  clearFilters(): void {
    this.dayControl.setValue(TuiDay.fromLocalNativeDate(new Date()));
    this.searchControl.setValue("");
    this.pageIndex = 1;
    this.loadPastLogs();
  }

  onPageChange(pageIndex: number): void {
    this.pageIndex = pageIndex;
    this.loadPastLogs();
  }

  formatDuration(seconds: number): string {
    if (!seconds || seconds <= 0) return "—";
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = Math.floor(seconds % 60);
    const parts: string[] = [];
    if (h > 0) parts.push(`${h}h`);
    if (m > 0) parts.push(`${m}m`);
    if (h === 0 && (s > 0 || m === 0)) parts.push(`${s}s`);
    return parts.join(" ");
  }
}
